import { supabase } from '../helpers/Supabase';
import { EmailService } from './emailService';
import { 
  ClientDelegate, 
  StoreDelegate, 
  DelegateAuth, 
  DelegateInvite, 
  DelegateStats,
  DelegateWithStore,
  DelegatePermissions
} from '../types/delegates';

export class DelegateService {
  // Client-level delegates
  static async getClientDelegates(clientId: string): Promise<ClientDelegate[]> {
    const { data, error } = await supabase
      .from('client_delegates')
      .select('*')
      .eq('client_id', clientId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  static async inviteClientDelegate(clientId: string, invite: DelegateInvite): Promise<ClientDelegate> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data: userRow } = await supabase
      .from('users')
      .select('id')
      .eq('auth_user_id', user.id)
      .single();

    // Get client name for email
    const { data: clientData } = await supabase
      .from('clients')
      .select('name')
      .eq('id', clientId)
      .single();

    const { data, error } = await supabase
      .from('client_delegates')
      .insert({
        client_id: clientId,
        email: invite.email,
        name: invite.name,
        role: invite.role,
        permissions: invite.permissions || {
          dashboard: true,
          settings: false,
          stores: true,
          reports: true
        },
        invited_by: userRow?.id
      })
      .select()
      .single();

    if (error) throw error;

    // Send invitation email
    try {
      // Create a custom invitation URL that doesn't rely on Supabase email confirmation
      const invitationUrl = `${window.location.origin}/delegate-signup?email=${encodeURIComponent(invite.email)}&type=client&ids=${data.id}`;
      
      await EmailService.sendDelegateInvite(
        invite,
        clientData?.name || 'Your Company',
        undefined, // No store names for client delegates
        invitationUrl
      );
    } catch (emailError) {
      console.error('Failed to send invitation email:', emailError);
      // Don't throw error - delegate was still created successfully
    }

    return data;
  }

  static async updateClientDelegate(delegateId: string, updates: Partial<ClientDelegate>): Promise<ClientDelegate> {
    const { data, error } = await supabase
      .from('client_delegates')
      .update(updates)
      .eq('id', delegateId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async deleteClientDelegate(delegateId: string): Promise<void> {
    const { error } = await supabase
      .from('client_delegates')
      .delete()
      .eq('id', delegateId);

    if (error) throw error;
  }

  // Store-level delegates
  static async getStoreDelegates(storeId?: string): Promise<DelegateWithStore[]> {
    let query = supabase
      .from('store_delegates')
      .select(`
        *,
        store:stores(id, store_name, client_id)
      `)
      .order('created_at', { ascending: false });

    if (storeId) {
      query = query.eq('store_id', storeId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }

  static async inviteStoreDelegates(storeIds: string[], invite: DelegateInvite): Promise<StoreDelegate[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data: userRow } = await supabase
      .from('users')
      .select('id')
      .eq('auth_user_id', user.id)
      .single();

    // Get store names and client name for email
    const { data: storesData } = await supabase
      .from('stores')
      .select(`
        id,
        store_name,
        client:clients(name)
      `)
      .in('id', storeIds);

    const delegates = storeIds.map(storeId => ({
      store_id: storeId,
      email: invite.email,
      name: invite.name,
      role: invite.role,
      permissions: invite.permissions || {
        dashboard: true,
        reports: true,
        chatbot: false
      },
      invited_by: userRow?.id
    }));

    const { data, error } = await supabase
      .from('store_delegates')
      .insert(delegates)
      .select();

    if (error) throw error;

    // Send invitation email
    try {
      const storeNames = storesData?.map(s => s.store_name) || [];
      const clientName = (storesData?.[0]?.client as any)?.name || 'Your Company';
      
      await EmailService.sendDelegateInvite(
        invite,
        clientName,
        storeNames,
        `${window.location.origin}/delegate-signup?email=${encodeURIComponent(invite.email)}&type=store&ids=${storeIds.join(',')}`
      );
    } catch (emailError) {
      console.error('Failed to send invitation email:', emailError);
      // Don't throw error - delegates were still created successfully
    }

    return data || [];
  }

  static async updateStoreDelegate(delegateId: string, updates: Partial<StoreDelegate>): Promise<StoreDelegate> {
    const { data, error } = await supabase
      .from('store_delegates')
      .update(updates)
      .eq('id', delegateId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async deleteStoreDelegate(delegateId: string): Promise<void> {
    const { error } = await supabase
      .from('store_delegates')
      .delete()
      .eq('id', delegateId);

    if (error) throw error;
  }

  // Delegate authentication
  static async getDelegateAuth(email: string): Promise<DelegateAuth | null> {
    const { data, error } = await supabase
      .from('delegate_auth')
      .select('*')
      .eq('email', email)
      .eq('is_active', true)
      .single();

    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows returned
    return data;
  }

  static async createDelegateAuth(delegateAuth: Partial<DelegateAuth>): Promise<DelegateAuth> {
    const { data, error } = await supabase
      .from('delegate_auth')
      .insert(delegateAuth)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  static async updateDelegateAuth(authId: string, updates: Partial<DelegateAuth>): Promise<DelegateAuth> {
    const { data, error } = await supabase
      .from('delegate_auth')
      .update(updates)
      .eq('id', authId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Statistics
  static async getDelegateStats(clientId: string): Promise<DelegateStats> {
    const [clientDelegates, storeDelegates] = await Promise.all([
      this.getClientDelegates(clientId),
      this.getStoreDelegates()
    ]);

    const totalDelegates = clientDelegates.length + storeDelegates.length;
    const activeDelegates = clientDelegates.filter(d => d.is_active).length + 
                           storeDelegates.filter(d => d.is_active).length;
    const pendingInvites = clientDelegates.filter(d => !d.accepted_at).length + 
                          storeDelegates.filter(d => !d.accepted_at).length;

    return {
      total_delegates: totalDelegates,
      active_delegates: activeDelegates,
      pending_invites: pendingInvites,
      client_delegates: clientDelegates.length,
      store_delegates: storeDelegates.length
    };
  }

  // Utility methods
  static async checkDelegateAccess(email: string, clientId: string): Promise<{
    hasAccess: boolean;
    delegateType: 'client' | 'store' | null;
    permissions?: any;
  }> {
    // Check client-level access
    const { data: clientDelegate } = await supabase
      .from('client_delegates')
      .select('*')
      .eq('email', email)
      .eq('client_id', clientId)
      .eq('is_active', true)
      .single();

    if (clientDelegate) {
      return {
        hasAccess: true,
        delegateType: 'client',
        permissions: clientDelegate.permissions
      };
    }

    // Check store-level access
    const { data: storeDelegates } = await supabase
      .from('store_delegates')
      .select('*')
      .eq('email', email)
      .eq('is_active', true);

    if (storeDelegates && storeDelegates.length > 0) {
      // Check if any of the stores belong to this client
      const storeIds = storeDelegates.map(d => d.store_id);
      const { data: stores } = await supabase
        .from('stores')
        .select('id')
        .in('id', storeIds)
        .eq('client_id', clientId);

      if (stores && stores.length > 0) {
        return {
          hasAccess: true,
          delegateType: 'store',
          permissions: storeDelegates[0].permissions // Use first store's permissions
        };
      }
    }

    return {
      hasAccess: false,
      delegateType: null
    };
  }

  // Email validation
  static validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Permission checking
  static hasPermission(delegate: ClientDelegate | StoreDelegate, permission: keyof DelegatePermissions): boolean {
    return delegate.permissions[permission] === true;
  }

  static canAccessDashboard(delegate: ClientDelegate | StoreDelegate): boolean {
    return this.hasPermission(delegate, 'dashboard');
  }

  static canAccessSettings(delegate: ClientDelegate | StoreDelegate): boolean {
    return this.hasPermission(delegate, 'settings');
  }

  static canAccessStores(delegate: ClientDelegate | StoreDelegate): boolean {
    return this.hasPermission(delegate, 'stores');
  }

  static canAccessReports(delegate: ClientDelegate | StoreDelegate): boolean {
    return this.hasPermission(delegate, 'reports');
  }

  static canAccessChatbot(delegate: StoreDelegate): boolean {
    return this.hasPermission(delegate, 'chatbot');
  }

  // Link user to client delegate record
  static async linkUserToClientDelegate(userId: string, delegateId: string): Promise<void> {
    const { error } = await supabase
      .from('client_delegates')
      .update({ 
        user_id: userId,
        accepted_at: new Date().toISOString()
      })
      .eq('id', delegateId);

    if (error) throw error;
  }

  // Link user to store delegate records
  static async linkUserToStoreDelegates(userId: string, storeIds: string[]): Promise<void> {
    const { error } = await supabase
      .from('store_delegates')
      .update({ 
        user_id: userId,
        accepted_at: new Date().toISOString()
      })
      .in('store_id', storeIds);

    if (error) throw error;
  }

  // Accept invitation (mark as active)
  static async acceptInvitation(delegateId: string, type: 'client' | 'store'): Promise<void> {
    const table = type === 'client' ? 'client_delegates' : 'store_delegates';
    
    const { error } = await supabase
      .from(table)
      .update({ 
        is_active: true,
        accepted_at: new Date().toISOString()
      })
      .eq('id', delegateId);

    if (error) throw error;
  }
} 